import React, { useMemo, useState } from 'react';
import { ApiGroupResponse, DetailPickerType } from './use-company-group-picker';
import { BottomModal } from '../../../components';
import { ReactComponent as SearchIcon } from '@zonofi/svg-icons/search-icon.svg';

type GroupItem = {
  id: string;
  name: string;
  emojiId?: string;
  isAutomatic?: boolean;
};
type GroupNode = {
  id: string;
  name: string;
  customerGroups?: GroupItem[];
  subFolders?: GroupNode[];
};
type GroupData = {
  folders: GroupNode[];
  rootCustomerGroups: GroupItem[];
} | null;

interface DetailPickerModalProps {
  isVisible?: boolean;
  type: DetailPickerType;
  groupData: GroupData | ApiGroupResponse;
  selectedValues: string[];
  searchText: string;
  setSearchText: (value: string) => void;
  onClose: () => void;
  onSubmit: (nextSelection: string[]) => void;
}

const formatLabel = (item: GroupItem) =>
  `${item?.emojiId ?? ''} ${item?.name ?? ''}`.trim();

const collectGroupIds = (nodes: GroupNode[] = []): string[] => {
  return nodes.flatMap((node) => [
    ...(node.customerGroups ?? []).map((group) => group.id),
    ...collectGroupIds(node.subFolders ?? []),
  ]);
};

export const DetailPickerModal: React.FC<DetailPickerModalProps> = (props) => {
  const {
    isVisible = false,
    type,
    groupData,
    selectedValues,
    searchText,
    setSearchText,
    onClose,
    onSubmit,
  } = props;
  const [localSelection, setLocalSelection] =
    useState<string[]>(selectedValues);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const title = {
    lifecycle: 'Life Cycle',
    segments: 'Segments',
    routes: 'Routes',
  }[type];
  const action = {
    lifecycle: 'Move',
    segments: 'Assign Segments',
    routes: 'Assign Routes',
  }[type];
  const showSearch = type !== 'lifecycle';
  const showSelectAll = type === 'segments';

  React.useEffect(
    () => setLocalSelection(selectedValues),
    [selectedValues, type]
  );

  const hasSelectionChanged = useMemo(() => {
    if (localSelection.length !== selectedValues.length) return true;
    const selectedSet = new Set(selectedValues);
    return localSelection.some((id) => !selectedSet.has(id));
  }, [localSelection, selectedValues]);

  const filterText = searchText.trim().toLowerCase();
  const include = (text: string) =>
    !filterText || text.toLowerCase().includes(filterText);
  const toggle = (id: string) =>
    setLocalSelection((prev) =>
      type === 'lifecycle' || type === 'routes'
        ? prev[0] === id
          ? []
          : [id]
        : prev.includes(id)
          ? prev.filter((v) => v !== id)
          : [...prev, id]
    );

  const sectionData = useMemo(() => {
    const roots = (groupData?.rootCustomerGroups ?? []).filter((item) =>
      include(formatLabel(item))
    );
    const folders = (groupData?.folders ?? []).filter((folder) => {
      if (include(folder.name)) return true;
      const hasGroup = (folder.customerGroups ?? []).some((g) =>
        include(formatLabel(g))
      );
      const hasChild = (folder.subFolders ?? []).some((s) => include(s.name));
      return hasGroup || hasChild;
    });
    return { roots, folders };
  }, [groupData, filterText]);

  const allSelectableIds = useMemo(() => {
    return [
      ...(groupData?.rootCustomerGroups ?? []).map((item) => item.id),
      ...collectGroupIds(groupData?.folders ?? []),
    ];
  }, [groupData]);

  const allSelected =
    allSelectableIds.length > 0 &&
    allSelectableIds.every((id) => localSelection.includes(id));

  const totalLabel = `${allSelectableIds.length} ${title}`;

  const groupMetaById = useMemo(() => {
    const map = new Map<string, GroupItem>();

    const collect = (items: GroupItem[] = []) => {
      items.forEach((item) => {
        if (item?.id) map.set(item.id, item);
      });
    };

    const walkFolders = (folders: GroupNode[] = []) => {
      folders.forEach((folder) => {
        collect(folder.customerGroups ?? []);
        walkFolders(folder.subFolders ?? []);
      });
    };

    collect(groupData?.rootCustomerGroups ?? []);
    walkFolders(groupData?.folders ?? []);

    return map;
  }, [groupData]);

  const selectedLifecycleMeta =
    type === 'lifecycle' ? groupMetaById.get(selectedValues?.[0] ?? '') : null;

  const isOptionDisabled = (item: GroupItem) => {
    if (type !== 'lifecycle') return false;
    if (typeof selectedLifecycleMeta?.isAutomatic !== 'boolean') return false;
    // Match web app behavior: only allow move within same lifecycle type.
    // manual -> manual, automatic -> automatic.
    if (selectedLifecycleMeta.isAutomatic !== !!item?.isAutomatic) return true;
    return false;
  };

  const onSelectAllClick = () => {
    if (!showSelectAll) return;
    if (allSelected) {
      setLocalSelection([]);
      return;
    }
    setLocalSelection(allSelectableIds);
  };

  const renderFolder = (node: GroupNode, level = 0): React.ReactNode => {
    const key = `${node.id}-${level}`;
    const isOpen = expanded[key] ?? true;
    const groups = (node.customerGroups ?? []).filter((item) =>
      include(formatLabel(item))
    );
    const children = (node.subFolders ?? []).filter((item) =>
      include(item.name)
    );
    if (!include(node.name) && !groups.length && !children.length) return null;
    return (
      <div className="company-details-screen__routes-modal-group">
        <div
          key={key}
          className="company-details-screen__tree-node"
          // style={{ paddingLeft: `${level * 14}px` }}
          style={{ '--level': level } as React.CSSProperties}
        >
          <button
            type="button"
            className="company-details-screen__tree-toggle"
            onClick={() => setExpanded((p) => ({ ...p, [key]: !isOpen }))}
          >
            <span>{isOpen ? '▾' : '▸'}</span>
            <span>{node.name}</span>
          </button>
          {isOpen && (
            <>
              {groups.map((item) => (
                <label
                  className="company-details-screen__modal-option"
                  style={{ '--level': level + 1 } as React.CSSProperties}
                  key={item.id}
                >
                  <input
                    type={type === 'lifecycle' ? 'radio' : 'checkbox'}
                    checked={localSelection.includes(item.id)}
                    disabled={isOptionDisabled(item)}
                    onChange={() => toggle(item.id)}
                  />
                  <span className="font-regular">{formatLabel(item)}</span>
                </label>
              ))}
              {children.map((child) => renderFolder(child, level + 1))}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <BottomModal
      isVisible={isVisible}
      showHeader={false}
      onLeftAction={onClose}
      modalContainerStyles={{
        borderRadius: '24px 24px 0 0',
        maxHeight: '82vh',
        height: 'min(82vh, 680px)',
      }}
      modalChildrenStyles={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div className="company-details-screen__modal-header">
        <button
          type="button"
          className="company-details-screen__modal-close font-semibold"
          onClick={onClose}
        >
          Close
        </button>
        <div className="company-details-screen__modal-title font-semibold">
          {title}
        </div>
        {showSelectAll ? (
          <button
            type="button"
            className="company-details-screen__modal-select-all font-semibold"
            onClick={onSelectAllClick}
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        ) : (
          <div className="company-details-screen__modal-spacer" />
        )}
      </div>
      <div className="company-details-screen__modal-body">
        {showSearch && (
          <div className="company-details-screen__search-wrap">
            <div className="company-details-screen__search-box">
              <SearchIcon />
              <div className="company-details-screen__search-divider" />
              <input
                type="text"
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="company-details-screen__search-input"
              />
            </div>
          </div>
        )}
        {showSearch && (
          <div className="company-details-screen__modal-count font-semibold">
            {totalLabel}
          </div>
        )}
        <div className="company-details-screen__modal-scroll">
          {sectionData.roots.map((item) => (
            <label
              className="company-details-screen__modal-option"
              key={item.id}
            >
              <input
                type={type === 'lifecycle' ? 'radio' : 'checkbox'}
                checked={localSelection.includes(item.id)}
                disabled={isOptionDisabled(item)}
                onChange={() => toggle(item.id)}
              />
              <span className="font-regular">{formatLabel(item)}</span>
            </label>
          ))}
          {type === 'routes'
            ? sectionData.folders.map((folder) => renderFolder(folder))
            : sectionData.folders.map((folder) => (
                <div
                  className="company-details-screen__modal-group"
                  key={folder.id}
                >
                  <div className="company-details-screen__group-title font-semibold">
                    {folder.name}
                  </div>
                  {(folder.customerGroups ?? [])
                    .filter((item) => include(formatLabel(item)))
                    .map((item) => (
                      <label
                        className="company-details-screen__modal-option"
                        key={item.id}
                      >
                        <input
                          type={type === 'lifecycle' ? 'radio' : 'checkbox'}
                          checked={localSelection.includes(item.id)}
                          disabled={isOptionDisabled(item)}
                          onChange={() => toggle(item.id)}
                        />
                        <span className="font-regular">
                          {formatLabel(item)}
                        </span>
                      </label>
                    ))}
                </div>
              ))}
          {!sectionData.roots.length && !sectionData.folders.length && (
            <div className="company-details-screen__empty font-regular">
              No data available.
            </div>
          )}
        </div>
        <div className="company-details-screen__modal-footer">
          <button
            type="button"
            className={`company-details-screen__modal-submit ${
              hasSelectionChanged
                ? 'company-details-screen__modal-submit--active'
                : ''
            } font-semibold`}
            disabled={!hasSelectionChanged}
            onClick={() => onSubmit(localSelection)}
          >
            {action}
          </button>
        </div>
      </div>
    </BottomModal>
  );
};
